import { useEffect, useState } from "react";
import { useUIStore, type ThemeMode } from "@/stores/ui-store";

/**
 * Returns the user's theme preference plus the *resolved* mode actually being
 * rendered. Components that need to branch on the real mode (e.g., picking a
 * logo variant) use `resolved`, not `theme`.
 */
export function useTheme() {
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const [resolved, setResolved] = useState<"light" | "dark">(() => {
    if (typeof document === "undefined") return "light";
    return (document.documentElement.getAttribute("data-theme") as "light" | "dark") ?? "light";
  });

  useEffect(() => {
    const update = () => {
      const next = document.documentElement.getAttribute("data-theme") as "light" | "dark";
      setResolved(next ?? "light");
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  return { theme, setTheme, resolved } as { theme: ThemeMode; setTheme: (t: ThemeMode) => void; resolved: "light" | "dark" };
}
